import React, { useCallback, useRef, useState } from 'react';
import { faBuildingCircleArrowRight, faCirclePlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Modal, OverlayTrigger, Spinner, Tooltip } from 'react-bootstrap-5';

import { fetchRemainingCredits, syncToCrm } from '../../../helpers/prospect_api';
import { displayToast } from 'bundles/connect/helpers/display_toast';
import IncludeOwnerDataCheckbox from '../add_to_rethink/include_owner_data_checkbox';

type AddPropertyToCrmProps = {
  asIcon?: boolean;
  insightsPropertyId: string;
  lastFetched: Date | null;
  propertySyncedAt?: string;
}

export default function AddPropertyToCrm({
  asIcon = false,
  insightsPropertyId,
  lastFetched,
  propertySyncedAt
}: AddPropertyToCrmProps) {
  const [show, setShow] = useState(false);
  const [saveContact, setSaveContact] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [syncTime, setSyncTime] = useState(propertySyncedAt);
  const [outOfCredits, setOutOfCredits] = useState<boolean | undefined>(undefined);

  const triggerRef = useRef(null);
  const cannotIncludeContacts = outOfCredits && !lastFetched;

  const handleModalDismiss = () => {
    setShow(false);
    setSaveContact(false);
    setLoading(false);
  };

  const crmSync = (includeContacts?: boolean) => {
    syncToCrm({
      insights_property_id: insightsPropertyId,
      include_contacts: includeContacts
    }).then((res) => {
      if (res.id) {
        displayToast('Successfully Saved Property', 'success');
        setSyncTime(new Date().toLocaleString());
      } else {
        displayToast('Something went wrong', 'error');
      }
      handleModalDismiss();
    }).finally(() => {
      setSaving(false);
    });
  };

  const handleSubmit = useCallback(() => {
    setSaving(true);
    crmSync(saveContact);
  }, [insightsPropertyId, saveContact]);

  const fetchCredits = () => {
    setOutOfCredits(undefined);
    setLoading(true);
    fetchRemainingCredits()
      .then((res) => {
        setOutOfCredits(res.remainingCredits <= 0);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  if (syncTime && !asIcon) {
    return <div className="text-muted small">Added at {syncTime}</div>;
  }


  const trigger = (
    <div className="align-content-center text-center h-100 p-2" ref={triggerRef} >
      <FontAwesomeIcon
        className="text-primary clickable squared"
        icon={faBuildingCircleArrowRight}
        onClick={() => setShow(true)}
      />
    </div>
  );

  const renderTooltip = (
    <Tooltip id={`add-to-list-tooltip-${insightsPropertyId}`}>
      Add Property
    </Tooltip>
  );


  return (
    <>
      <Modal show={show} onHide={handleModalDismiss} onShow={fetchCredits}>
        <Modal.Header className="flex-row">
          <Modal.Title>
            Add Property
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {loading ? (
            <div className="text-center py-4">
              <Spinner animation="border" />
            </div>
          ) : (
            <IncludeOwnerDataCheckbox
              cannotIncludeContacts={cannotIncludeContacts}
              checked={saveContact}
              disabled={saving || cannotIncludeContacts}
              onChange={() => setSaveContact(!saveContact)}
            />
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button disabled={saving || loading} variant="secondary" onClick={() => handleSubmit()}>
            Save Property
            {saving && (
              <div className="text-center py-4">
                <Spinner animation="border" size="sm" />
              </div>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {asIcon ?
        (
          <OverlayTrigger
            overlay={renderTooltip}
            placement="right"
            popperConfig={{
              modifiers: [
                {
                  name: 'offset',
                  options: {
                    offset: [0, 8],
                  },
                }
              ]
            }}
          >
            {trigger}
          </OverlayTrigger>
        ) :
        (
          <Button variant="text-primary" onClick={() => setShow(true)}>
            <FontAwesomeIcon icon={faCirclePlus} />
            Add as Property
          </Button>
        )}
    </>
  );
}
